import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box, Button, InputAdornment, Popover, Stack, TextField } from '@mui/material';
import { CalendarPicker, PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { FC, useEffect, useRef, useState } from 'react';

dayjs.extend(isBetween);

type DateRangeSelectorProps = {
  startDate: Dayjs;
  endDate: Dayjs;
  onChange: (startDate: Dayjs, endDate: Dayjs) => void;
  label?: string;
  format?: string;
  sx?: object;
};

const formatRange = (start: Dayjs, end: Dayjs, format: string) =>
  `${start.format(format)} – ${end.format(format)}`;

const DateRangeSelector: FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  onChange,
  label = 'Date range',
  format = 'D MMM YYYY',
  sx,
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState<Dayjs>(startDate);
  const [draftEnd, setDraftEnd] = useState<Dayjs | null>(endDate);
  const [selectingEnd, setSelectingEnd] = useState(false);

  useEffect(() => {
    if (!open) {
      setDraftStart(startDate);
      setDraftEnd(endDate);
      setSelectingEnd(false);
    }
  }, [endDate, open, startDate]);

  const handleDayClick = (day: Dayjs) => {
    const next = day.startOf('day');
    if (!selectingEnd || !draftStart) {
      setDraftStart(next);
      setDraftEnd(null);
      setSelectingEnd(true);
      return;
    }

    if (next.isBefore(draftStart, 'day')) {
      setDraftStart(next);
      setDraftEnd(draftStart);
    } else {
      setDraftEnd(next);
    }
    setSelectingEnd(false);
  };

  const handleApply = () => {
    const nextEnd = draftEnd ?? draftStart;
    const start = draftStart.isAfter(nextEnd, 'day') ? nextEnd : draftStart;
    const end = draftStart.isAfter(nextEnd, 'day') ? draftStart : nextEnd;
    onChange(start.startOf('day'), end.startOf('day'));
    setOpen(false);
  };

  const renderDay = (day: Dayjs, _selectedDays: Dayjs[], pickersDayProps: PickersDayProps<Dayjs>) => {
    const rangeEnd = draftEnd ?? (selectingEnd ? null : draftStart);
    const isStart = day.isSame(draftStart, 'day');
    const isEnd = rangeEnd != null && day.isSame(rangeEnd, 'day');
    const inRange =
      rangeEnd != null &&
      !isStart &&
      !isEnd &&
      day.isBetween(draftStart, rangeEnd, 'day', '()');

    return (
      <PickersDay
        {...pickersDayProps}
        onDaySelect={handleDayClick}
        selected={isStart || isEnd}
        sx={{
          ...(inRange && {
            backgroundColor: 'action.selected',
            borderRadius: 0,
          }),
          ...((isStart || isEnd) && {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { backgroundColor: 'primary.dark' },
          }),
          ...(isStart && rangeEnd != null && !isEnd && { borderTopRightRadius: 0, borderBottomRightRadius: 0 }),
          ...(isEnd && !isStart && { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }),
        }}
      />
    );
  };

  return (
    <>
      <TextField
        ref={anchorRef}
        label={label}
        size="small"
        value={formatRange(startDate, endDate, format)}
        onClick={() => setOpen(true)}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <CalendarMonthIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 160,
          width: '100%',
          cursor: 'pointer',
          '& .MuiInputBase-root': {
            fontSize: '0.75rem',
            minHeight: 32,
            cursor: 'pointer',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.75rem',
          },
          ...sx,
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box sx={{ p: 1.5 }}>
          <CalendarPicker date={draftStart} onChange={() => undefined} renderDay={renderDay} />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1, px: 1 }}>
            <Button size="small" color="inherit" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleApply} disabled={!draftStart}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangeSelector;
